import React from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, AlertTriangle, X } from "lucide-react";
import { useDeleteTeam } from "@/hooks/api-hooks";

interface Team {
  id: string;
  name: string;
  description?: string;
  memberCount?: number;
}

interface DeleteTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
  onSuccess?: () => void;
}

export function DeleteTeamModal({
  isOpen,
  onClose,
  team,
  onSuccess
}: DeleteTeamModalProps) {
  const [confirmationInput, setConfirmationInput] = React.useState("");
  const deleteTeamMutation = useDeleteTeam();

  const expectedConfirmation = team?.name || "";
  const isConfirmationValid = confirmationInput.trim() === expectedConfirmation;

  React.useEffect(() => {
    if (!isOpen) {
      setConfirmationInput("");
    }
  }, [isOpen]);

  const handleDelete = async () => {
    if (!team || !isConfirmationValid) return;

    deleteTeamMutation.mutate(
      team.id,
      {
        onSuccess: () => {
          toast.success(`Team "${team.name}" deleted successfully!`);

          onSuccess?.();
          onClose();
          setConfirmationInput("");
        },
        onError: (error: any) => {
          console.error("Error deleting team:", error);
          toast.error(`Failed to delete team: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    setConfirmationInput("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-card text-card-foreground border border-border shadow-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-foreground">
              Delete Team
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Warning Banner */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-1">
                  This action cannot be undone
                </h3>
                <p className="text-sm text-red-700">
                  Deleting this team will permanently remove all team data, including member assignments and history.
                  {team?.memberCount && team.memberCount > 0 && (
                    <span className="block mt-1 font-medium">
                      This team currently has {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Team Info */}
          {team && (
            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="font-medium text-foreground mb-1">{team.name}</h4>
              {team.description && (
                <p className="text-sm text-muted-foreground">{team.description}</p>
              )}
              {team.memberCount !== undefined && (
                <p className="text-sm text-muted-foreground mt-2">
                  {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}

          {/* Confirmation Input */}
          <div className="space-y-2">
            <Label htmlFor="confirmation">
              Type <span className="font-mono font-bold">{expectedConfirmation}</span> to confirm:
            </Label>
            <Input
              id="confirmation"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              placeholder={expectedConfirmation}
              className={
                confirmationInput && !isConfirmationValid
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : ""
              }
            />
            {confirmationInput && !isConfirmationValid && (
              <p className="text-sm text-red-600">
                Team name doesn't match. Please type exactly: {expectedConfirmation}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={deleteTeamMutation.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteTeamMutation.isPending || !isConfirmationValid}
              variant="destructive"
            >
              {deleteTeamMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Team
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}